import { FC, useMemo } from 'react';
import { TextKey } from '@mews-commander/enums';
import { useLocalizeProperty, useLocalizeText } from '@mews/framework';
import { Banner } from '@optimus-web/core';
import { SelectField } from '@mews-commander/form';
import { createOption } from '@mews-commander/ui';
import { FormFields } from '../../../schema';
import { useGetServices } from '../../../queries/useGetServices';

interface ServiceSelectionFieldProps {
    enterpriseId: string;
    serviceId: string;
    isEditMode: boolean;
}

export const ServiceSelectionField: FC<ServiceSelectionFieldProps> = ({ enterpriseId, serviceId, isEditMode }) => {
    const lt = useLocalizeText();
    const lp = useLocalizeProperty();
    const { data, isFetching, isFetched } = useGetServices(enterpriseId, serviceId);

    const options =
        data?.map((opt) =>
            createOption({
                label: lp(opt.names),
                value: opt.id,
                ...(opt.timeUnitLabelKey && { secondaryText: lt({ textKey: opt.timeUnitLabelKey }) }),
            })
        ) ?? [];

    const showWarning = useMemo(() => isFetched && data?.length === 0, [data, isFetched]);
    const bookablePeriodString = `${lt({ textKey: TextKey.NightPlural })}, ${lt({ textKey: TextKey.DayPlural })}, ${lt({
        textKey: TextKey.HourPlural,
    })}`;

    return (
        <>
            {showWarning ? (
                <Banner
                    variant="error"
                    primaryText={lt({
                        textKey: TextKey.PromotedServiceAssignmentAnotherServiceWithAllowedPeriodRequiredErrorMessage,
                        substitutes: { commaSeparatedAllowedPeriods: bookablePeriodString },
                    })}
                />
            ) : null}
            <SelectField
                name={FormFields.PromotedServiceId}
                label={lt({ textKey: TextKey.PromotedService })}
                options={options}
                loader={isFetching}
                placeholder={lt({
                    textKey: TextKey.PromotedServiceAssignmentFormPromotedServicePlaceholder,
                })}
                showEmptyValue={false}
                required
                readOnly={isEditMode}
            />
        </>
    );
};
